<template>
  <div class="flex flex-col lg:-mt-nav-lg lg:h-screen">
    <div
      class="relative overflow-hidden bg-gray-10 max-lg:flex-col-reverse lg:grow"
    >
      <div class="relative h-full">
        <ShopCategoryBrandLogo
          :category="category"
          class="absolute left-4 max-lg:bottom-4 lg:left-20 lg:top-36"
        />
        <h2
          class="absolute right-2 top-8 text-center text-xl font-bold max-sm:px-2 sm:right-0 sm:w-3/4 md:w-1/2 md:text-3xl lg:top-[50vh] lg:w-1/3 lg:-translate-y-1/2 lg:text-6xl"
          :class="[
            data.text_color_button?.value === 'white'
              ? 'text-white'
              : 'text-black',
            { 'left-4 lg:left-20': data.text_position?.value === 'left' },
            {
              'right-4 lg:right-20': data.text_position?.value === 'right',
            },
            {
              'left-1/2 -translate-x-1/2':
                data.text_position?.value === 'center',
            },
          ]"
          v-html="data.text?.replace(/\n/g, '<br />')"
        />

        <video
          v-if="
            data.show_image_or_video?.value === 'video' && data.video?.permalink
          "
          autoplay
          muted
          loop
          playsinline
          class="max-h-dvh w-full object-cover lg:size-full"
        >
          <source :src="data.video?.permalink" type="video/mp4" />
        </video>
        <AppImage
          v-else-if="data.background_image"
          :asset="data.background_image"
          sizes="xs:100vw sm:100vw md:100vw lg:100vw xl:100vw 2xl:100vw 3xl:100vw"
          :class="{ 'max-lg:hidden': data.mobile_image }"
          class="max-h-dvh w-full object-cover lg:size-full"
        />
        <AppImage
          v-if="
            data.mobile_image && data.show_image_or_video?.value !== 'video'
          "
          :asset="data.mobile_image"
          sizes="xs:100vw sm:100vw md:100vw"
          class="max-h-dvh w-full object-cover lg:hidden"
        />
      </div>
      <div
        class="w-full lg:absolute lg:bottom-0 lg:left-0"
        :class="[
          data.text_color_button?.value === 'white'
            ? 'bg-black lg:bg-transparent'
            : 'bg-white lg:bg-transparent',
        ]"
      >
        <div
          class="flex p-4 max-lg:gap-10 max-sm:flex-col lg:items-end lg:justify-between"
        >
          <div class="lg:w-1/2 lg:pb-16 lg:pl-16">
            <ShopCategoryExclusiveBadge />
            <div class="mb-2 mt-4 flex items-center gap-2 lg:gap-4">
              <IconLock
                class="h-6 lg:h-12"
                :class="[
                  data.text_color_button?.value === 'white'
                    ? 'text-white'
                    : 'text-black',
                ]"
              />
              <h1
                class="text-2xl font-bold lg:text-5xl"
                :class="[
                  data.text_color_button?.value === 'white'
                    ? 'text-white'
                    : 'text-black',
                ]"
              >
                {{ useUseGetCategoryName(category) }}
              </h1>
            </div>
            <Bard
              class="text-sm font-semibold lg:text-md"
              :class="[
                data.text_color_button?.value === 'white'
                  ? 'text-white'
                  : 'text-black',
              ]"
              :content="getTranslatedProperty(category, 'description')"
            />
          </div>
          <div class="absolute z-0 flex w-full justify-center max-lg:hidden">
            <ScrollDown />
          </div>
          <div class="max-w-[40rem] lg:w-1/2">
            <div class="lg:ml-auto lg:w-2/3">
              <div class="bg-black px-8 py-6 text-white">
                <p class="font-bold">
                  {{ $t('category.brand.exclusivePublicCtaTitle') }}
                </p>
                <p class="mb-6 mt-2 text-sm font-medium">
                  {{ $t('category.brand.exclusivePublicCtaText') }}
                </p>
                <AppButton
                  :to="getShopRoute('/account/brands')"
                  variant="secondary"
                  icon="lock"
                >
                  {{ $t('category.brand.requestAccess') }}
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      :class="[
        data.text_color_button?.value === 'black' ? 'bg-white' : ' bg-black',
      ]"
    >
      <Container class="w-full" indent="tight">
        <div class="flex h-16 shrink-0 items-center justify-start max-lg:pt-4">
          <Breadcrumbs
            :is-white-text="data.text_color_button?.value === 'white'"
          />
        </div>
      </Container>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'
import type { AssetFragment } from '#gql'

defineProps<{
  category: Schemas['Category']
  data: {
    background_image: AssetFragment
    text_position: {
      value: 'left' | 'right' | 'center'
    }
    mobile_image?: AssetFragment
    text: string
    text_color_button: {
      value: 'white' | 'black'
    }
    show_image_or_video: {
      value: 'image' | 'video'
    }
    video?: AssetFragment
  }
}>()

const { getShopRoute } = useShopRouter()
</script>
